// Atrium Resident Portal - Valet Requests Page
// Canonical Schema: locations/{locationId}/valetRequests/{requestId}
import { useState, useEffect } from "react";
import ResidentValetFeedback from "./components/ResidentValetFeedback";
import { useAuth } from "./AuthContext";
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import {
  ValetRequest,
  ValetRequestType,
  ValetRequestStatus,
  REQUEST_TYPE_LABELS,
  STATUS_LABELS,
  REQUEST_TYPE_ICONS,
  ValetRequestPriority,
} from "./api";

type Tab = "all" | "pending" | "in_progress" | "completed" | "cancelled";

interface ValetRequestsPageProps {
  locationId: string;
}

function getStatusColor(status: ValetRequestStatus): { bg: string; fg: string } {
  switch (status) {
    case "pending":
      return { bg: "var(--azzurro-100)", fg: "var(--azzurro-700)" };
    case "assigned":
      return { bg: "var(--azzurro-200)", fg: "var(--azzurro-800)" };
    case "in_progress":
      return { bg: "var(--azzurro-200)", fg: "var(--azzurro-800)" };
    case "completed":
      return { bg: "rgba(5, 150, 105, 0.12)", fg: "var(--success)" };
    case "cancelled":
      return { bg: "rgba(107, 114, 128, 0.12)", fg: "var(--stone)" };
    default:
      return { bg: "var(--azzurro-100)", fg: "var(--azzurro-700)" };
  }
}

function getPriorityColor(priority: ValetRequestPriority): { bg: string; fg: string } {
  switch (priority) {
    case "urgent":
      return { bg: "rgba(239, 68, 68, 0.15)", fg: "#DC2626" };
    case "low":
      return { bg: "rgba(156, 163, 175, 0.15)", fg: "#5B7280" };
    default:
      return { bg: "rgba(107, 114, 128, 0.15)", fg: "#374151" };
  }
}

function formatTime(timestamp: Timestamp | string | null | undefined): string {
  if (!timestamp) return "";

  const date = typeof timestamp === "string" ? new Date(timestamp) : (timestamp as Timestamp).toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 43200) return `${Math.floor(diffMins / 1440)}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatRequestedTime(requestedForAt: string): string {
  const date = new Date(requestedForAt);
  const now = new Date();
  const isFuture = date > now;

  if (isFuture) {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
           " at " + date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }

  return "Now";
}

export default function ValetRequestsPage({ locationId }: ValetRequestsPageProps) {
  const { token } = useAuth();
  const residentId = token?.residentId ?? "";
  const [tab, setTab] = useState<Tab>("all");
  const [requests, setRequests] = useState<ValetRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ValetRequest | null>(null);

  // Fetch requests from canonical Firestore path
  useEffect(() => {
    if (!locationId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    if (!residentId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    let requestsQuery;
    const col = collection(db, `locations/${locationId}/valetRequests`);

    switch (tab) {
      case "pending":
        requestsQuery = query(
          col,
          where("residentId", "==", residentId),
          where("status", "==", "pending"),
          orderBy("requestedForAt", "asc")
        );
        break;
      case "in_progress":
        requestsQuery = query(
          col,
          where("residentId", "==", residentId),
          where("status", "==", "in_progress"),
          orderBy("requestedForAt", "asc")
        );
        break;
      case "completed":
        requestsQuery = query(
          col,
          where("residentId", "==", residentId),
          where("status", "==", "completed"),
          orderBy("completedAt", "desc")
        );
        break;
      case "cancelled":
        requestsQuery = query(
          col,
          where("residentId", "==", residentId),
          where("status", "==", "cancelled"),
          orderBy("updatedAt", "desc")
        );
        break;
      default: // all
        requestsQuery = query(
          col,
          where("residentId", "==", residentId),
          orderBy("requestedForAt", "desc")
        );
        break;
    }

    const unsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requestsList: ValetRequest[] = [];
      snapshot.forEach((doc) => {
        requestsList.push({
          id: doc.id,
          ...doc.data(),
        } as ValetRequest);
      });
      setRequests(requestsList);
      setLoading(false);
    }, (err) => {
      console.error("REQUESTS_LOAD_ERROR", err);
      setError("Failed to load requests. Please try again.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [locationId, residentId, tab]);

  // Filter requests based on tab
  const filteredRequests = requests.filter((request) => {
    switch (tab) {
      case "pending":
        return request.status === "pending";
      case "in_progress":
        return request.status === "in_progress";
      case "completed":
        return request.status === "completed";
      case "cancelled":
        return request.status === "cancelled";
      default: // all
        return true;
    }
  });

  // Sort: Future scheduled first, then by requestedForAt
  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const aTime = new Date(a.requestedForAt).getTime();
    const bTime = new Date(b.requestedForAt).getTime();
    const now = Date.now();
    const aIsFuture = aTime > now;
    const bIsFuture = bTime > now;

    if (aIsFuture && !bIsFuture) return -1;
    if (!aIsFuture && bIsFuture) return 1;
    return aTime - bTime;
  });

  const summary = {
    all: requests.length,
    pending: requests.filter(r => r.status === "pending").length,
    inProgress: requests.filter(r => r.status === "in_progress").length,
    completed: requests.filter(r => r.status === "completed").length,
    urgent: requests.filter(r => r.priority === "urgent" && ["pending", "assigned", "in_progress"].includes(r.status)).length,
  };

  // Load request events when detail modal is open
  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    if (!selectedRequest || !locationId) {
      setEvents([]);
      return;
    }

    setEventsLoading(true);
    const eventsQuery = query(
      collection(db, `locations/${locationId}/valetRequests/${selectedRequest.id}/events`),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      const eventsList: any[] = [];
      snapshot.forEach((doc) => {
        eventsList.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setEvents(eventsList);
      setEventsLoading(false);
    }, (err) => {
      console.error("EVENTS_LOAD_ERROR", err);
      setEventsLoading(false);
    });

    return () => unsubscribe();
  }, [selectedRequest?.id, locationId]);

  return (
    <div className="section" style={{ background: "var(--sand)" }}>
      <div className="container">
        {/* Page Header */}
        <div style={{
          marginBottom: "var(--space-3xl)",
          textAlign: "center",
        }}>
          <h1 style={{
            fontSize: "var(--text-4xl)",
            fontWeight: 600,
            color: "var(--ink)",
            marginBottom: "var(--space-md)",
            fontFamily: "var(--font-body)",
          }}>
            Valet Requests
          </h1>
          <p style={{
            fontSize: "var(--text-lg)",
            color: "var(--stone)",
            fontFamily: "var(--font-body)",
            marginBottom: "var(--space-lg)",
          }}>
            View and manage your valet service requests
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "var(--space-md)",
          marginBottom: "var(--space-2xl)",
        }}>
          <SummaryCard label="All" count={summary.all} color="var(--ink)" />
          <SummaryCard label="Pending" count={summary.pending} color="var(--azzurro-600)" />
          <SummaryCard label="In Progress" count={summary.inProgress} color="var(--azzurro-800)" />
          <SummaryCard label="Completed" count={summary.completed} color="var(--success)" />
          <SummaryCard label="Urgent" count={summary.urgent} color="#DC2626" />
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "var(--space-sm)",
          marginBottom: "var(--space-2xl)",
        }}>
          <TabButton
            active={tab === "all"}
            onClick={() => setTab("all")}
            label="All"
            count={summary.all}
          />
          <TabButton
            active={tab === "pending"}
            onClick={() => setTab("pending")}
            label="Pending"
            count={summary.pending}
          />
          <TabButton
            active={tab === "in_progress"}
            onClick={() => setTab("in_progress")}
            label="In Progress"
            count={summary.inProgress}
          />
          <TabButton
            active={tab === "completed"}
            onClick={() => setTab("completed")}
            label="Completed"
            count={summary.completed}
          />
          <TabButton
            active={tab === "cancelled"}
            onClick={() => setTab("cancelled")}
            label="Cancelled"
          count={requests.filter(r => r.status === "cancelled").length}
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{
            padding: "var(--space-3xl)",
            textAlign: "center",
            color: "var(--stone)",
          }}>
            Loading requests...
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{
            padding: "var(--space-3xl)",
            textAlign: "center",
            color: "var(--danger)",
          }}>
            {error}
          </div>
        )}

        {/* Requests List */}
        {!loading && !error && sortedRequests.length === 0 && (
          <div style={{
            padding: "var(--space-5xl)",
            textAlign: "center",
            color: "var(--stone)",
          }}>
            <div style={{ fontSize: "48px", marginBottom: "var(--space-md)" }}>📋</div>
            <h2 style={{
              fontSize: "var(--text-2xl)",
              color: "var(--stone)",
              marginBottom: "var(--space-md)",
            }}>
              No requests
            </h2>
            <p style={{
              fontSize: "var(--text-base)",
              color: "var(--stone)",
              marginBottom: "var(--space-2xl)",
            }}>
              {tab === "all"
                ? "When you submit a valet request, it will appear here."
                : `No ${STATUS_LABELS[tab as keyof typeof STATUS_LABELS]?.toLowerCase() || ""} requests yet.`}
            </p>
            <button
              style={{
                padding: "var(--space-sm) var(--space-xl)",
                background: "var(--azzurro-700)",
                color: "var(--white)",
                border: "none",
                borderRadius: "var(--radius-md)",
                fontSize: "var(--text-base)",
                fontWeight: 500,
                cursor: "pointer",
                marginTop: "var(--space-xl)",
              }}
              onClick={() => setTab("all")}
            >
              View All Requests
            </button>
          </div>
        )}

        {!loading && !error && sortedRequests.length > 0 && (
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-lg)",
          }}>
            {sortedRequests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                onClick={() => setSelectedRequest(request)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Request Detail Modal */}
      {selectedRequest && (
        <DetailModal
          request={selectedRequest}
          events={events}
          eventsLoading={eventsLoading}
          locationId={locationId}
          residentId={residentId}
          onClose={() => setSelectedRequest(null)}
        />
      )}

      <style>{`
        .summary-card {
          padding: var(--space-xl);
          border-radius: var(--radius-lg);
          background: var(--white);
          border: 1px solid var(--line);
          text-align: center;
        }

        .summary-card-label {
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--stone);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: var(--space-xs);
        }

        .summary-card-count {
          font-size: var(--text-2xl);
          font-weight: 700;
          color: var(--azzurro-700);
        }

        .tab-button {
          padding: var(--space-sm) var(--space-xl);
          border: 1px solid var(--line);
          border-radius: var(--radius-md);
          background: var(--white);
          color: var(--stone);
          font-size: var(--text-base);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .tab-button:hover {
          background: var(--sand);
        }

        .tab-button.active {
          background: var(--azzurro-700);
          color: var(--white);
          border-color: var(--azzurro-700);
        }

        .request-card {
          padding: var(--space-xl);
          border-radius: var(--radius-lg);
          background: var(--white);
          border: 1px solid var(--line);
          transition: all 0.2s;
        }

        .request-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--space-lg);
        }

        .modal-content {
          background: var(--white);
          border-radius: var(--radius-xl);
          width: 100%;
          max-width: 500px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        }

        .event-item {
          padding: var(--space-sm);
          border-left: 3px solid var(--line);
          margin-bottom: var(--space-md);
        }

        .event-item.created { border-left-color: var(--azzurro-500); }
        .event-item.accepted { border-left-color: var(--azzurro-600); }
        .event-item.started { border-left-color: var(--azzurro-800); }
        .event-item.completed { border-left-color: var(--success); }
        .event-item.cancelled { border-left-color: var(--danger); }
      `}</style>
    </div>
  );
}

// ==================== SUBCOMPONENTS ====================

function SummaryCard({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className="summary-card">
      <div className="summary-card-label">{label}</div>
      <div className="summary-card-count">{count}</div>
    </div>
  );
}

function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number }) {
  return (
    <button className={`tab-button ${active ? "active" : ""}`} onClick={onClick}>
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span style={{
          marginLeft: "8px",
          background: active ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.08)",
          color: active ? "var(--white)" : "var(--azzurro-700)",
          padding: "2px 8px",
          borderRadius: "var(--radius-pill)",
          fontSize: "var(--text-xs)",
          fontWeight: 600,
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

function RequestCard({ request, onClick }: { request: ValetRequest; onClick: () => void }) {
  const statusColors = getStatusColor(request.status);
  const priorityColors = getPriorityColor(request.priority);
  const statusLabel = STATUS_LABELS[request.status];
  const requestedTimeLabel = formatRequestedTime(request.requestedForAt);
  const isScheduled = new Date(request.requestedForAt) > new Date();

  return (
    <div className="request-card" onClick={onClick}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "var(--space-md)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
          <span style={{ fontSize: "24px" }}>
            {REQUEST_TYPE_ICONS[request.requestType] || "📋"}
          </span>
          <div>
            <div style={{
              fontSize: "var(--text-lg)",
              fontWeight: 600,
              color: "var(--ink)",
              marginBottom: "2px",
            }}>
              {REQUEST_TYPE_LABELS[request.requestType]}
            </div>
            <div style={{
              fontSize: "var(--text-sm)",
              color: "var(--stone)",
            }}>
              Unit {request.unitNumber}
            </div>
          </div>
        </div>

        {/* Priority Badge */}
        {request.priority === "urgent" && (
          <span style={{
            padding: "4px 10px",
            borderRadius: "var(--radius-sm)",
            background: priorityColors.bg,
            color: priorityColors.fg,
            fontSize: "var(--text-xs)",
            fontWeight: 600,
          }}>
            URGENT
          </span>
        )}

        {/* Scheduled Badge */}
        {isScheduled && (
          <span style={{
            padding: "4px 10px",
            borderRadius: "var(--radius-sm)",
            background: "rgba(217, 119, 6, 0.12)",
            color: "var(--warning)",
            fontSize: "var(--text-xs)",
            fontWeight: 600,
          }}>
            SCHEDULED
          </span>
        )}

        {/* Status */}
        <span
          style={{
            background: statusColors.bg,
            color: statusColors.fg,
            padding: "6px 14px",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--text-sm)",
            fontWeight: 600,
          }}
        >
          {statusLabel}
        </span>
      </div>

      {/* Time Info */}
      <div style={{
        fontSize: "var(--text-sm)",
        color: "var(--stone)",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}>
        <div>Requested: {requestedTimeLabel}</div>
        {request.startedAt && (
          <div>Started: {formatTime(request.startedAt)}</div>
        )}
        {request.completedAt && (
          <div>Completed: {formatTime(request.completedAt)}</div>
        )}
      </div>

      {/* Vehicle */}
      {request.vehicleLabel && (
        <div style={{
          fontSize: "var(--text-sm)",
          color: "var(--stone)",
          marginTop: "var(--space-sm)",
        }}>
          {request.vehicleLabel}
        </div>
      )}

      {/* Message */}
      <div style={{
        fontSize: "var(--text-base)",
        color: "var(--ink)",
        marginTop: "var(--space-sm)",
        marginBottom: "var(--space-md)",
        lineHeight: 1.5,
      }}>
        {request.message}
      </div>

      {/* Assigned To */}
      {request.assignedToName && (
        <div style={{
          fontSize: "var(--text-sm)",
          color: "var(--stone)",
          marginTop: "var(--space-sm)",
        }}>
          Assigned to: {request.assignedToName}
        </div>
      )}
    </div>
  );
}

interface DetailModalProps {
  request: ValetRequest;
  events: any[];
  eventsLoading: boolean;
  locationId: string;
  residentId: string;
  onClose: () => void;
}

function DetailModal({ request, events, eventsLoading, locationId, residentId, onClose }: DetailModalProps) {
  const statusColors = getStatusColor(request.status);
  const statusLabel = STATUS_LABELS[request.status];

  const getEventLabel = (type: string): string => {
    switch (type) {
      case "created":
        return "Request Created";
      case "accepted":
        return "Request Accepted";
      case "delegated":
        return "Request Delegated";
      case "started":
        return "In Progress";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Cancelled";
      default:
        return type;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: "var(--space-xl)",
          borderBottom: "1px solid var(--line)",
          marginBottom: "var(--space-lg)",
        }}>
          <h2 style={{
            fontSize: "var(--text-xl)",
            fontWeight: 600,
            color: "var(--ink)",
            margin: 0,
          }}>
            {REQUEST_TYPE_LABELS[request.requestType]}
          </h2>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              fontSize: "20px",
              color: "var(--stone)",
            }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Details */}
        <div style={{ padding: "0 var(--space-xl)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-md)", marginBottom: "var(--space-lg)" }}>
            <div>
              <label style={{
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                color: "var(--stone)",
                display: "block",
                marginBottom: "var(--space-xs)",
              }}>
                Unit
              </label>
              <div style={{
                fontSize: "var(--text-base)",
                fontWeight: 600,
                color: "var(--ink)",
              }}>
                {request.unitNumber}
              </div>
            </div>
            <div>
              <label style={{
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                color: "var(--stone)",
                display: "block",
                marginBottom: "var(--space-xs)",
              }}>
                Status
              </label>
              <div style={{
                padding: "6px 14px",
                borderRadius: "var(--radius-md)",
                background: statusColors.bg,
                color: statusColors.fg,
                fontWeight: 600,
              }}>
                {statusLabel}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-md)", marginBottom: "var(--space-lg)" }}>
            <div>
              <label style={{
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                color: "var(--stone)",
                display: "block",
                marginBottom: "var(--space-xs)",
              }}>
                Priority
              </label>
              <div style={{
                padding: "6px 14px",
                borderRadius: "var(--radius-md)",
                background: getPriorityColor(request.priority).bg,
                color: getPriorityColor(request.priority).fg,
                fontWeight: 600,
                textTransform: "capitalize",
              }}>
                {request.priority}
              </div>
            </div>
            <div>
              <label style={{
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                color: "var(--stone)",
                display: "block",
                marginBottom: "var(--space-xs)",
              }}>
                Requested For
              </label>
              <div style={{
                fontSize: "var(--text-base)",
                color: "var(--ink)",
              }}>
                {formatRequestedTime(request.requestedForAt)}
              </div>
            </div>
          </div>

          {request.vehicleLabel && (
            <div style={{ marginBottom: "var(--space-lg)" }}>
              <label style={{
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                color: "var(--stone)",
                display: "block",
                marginBottom: "var(--space-xs)",
              }}>
                Vehicle
              </label>
              <div style={{
                fontSize: "var(--text-base)",
                color: "var(--ink)",
                padding: "var(--space-sm)",
                borderRadius: "var(--radius-md)",
                background: "var(--sand)",
              }}>
                {request.vehicleLabel}
              </div>
            </div>
          )}

          {request.assignedToName && (
            <div style={{ marginBottom: "var(--space-lg)" }}>
              <label style={{
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                color: "var(--stone)",
                display: "block",
                marginBottom: "var(--space-xs)",
              }}>
                Assigned To
              </label>
              <div style={{
                fontSize: "var(--text-base)",
                color: "var(--ink)",
              }}>
                {request.assignedToName}
              </div>
            </div>
          )}

          <div style={{ marginBottom: "var(--space-lg)" }}>
            <label style={{
              fontSize: "var(--text-sm)",
              fontWeight: 500,
              color: "var(--stone)",
              display: "block",
              marginBottom: "var(--space-xs)",
            }}>
              Message
            </label>
            <div style={{
              fontSize: "var(--text-base)",
              color: "var(--ink)",
              lineHeight: 1.5,
              padding: "var(--space-sm)",
              borderRadius: "var(--radius-md)",
              background: "var(--sand)",
            }}>
              {request.message}
            </div>
          </div>
        </div>

        {/* Event Timeline */}
        <div style={{ padding: "0 var(--space-xl)" }}>
          <h3 style={{
            fontSize: "var(--text-lg)",
            fontWeight: 600,
            color: "var(--ink)",
            marginBottom: "var(--space-md)",
          }}>
            Event Timeline
          </h3>
          {eventsLoading ? (
            <div style={{
              padding: "var(--space-xl)",
              textAlign: "center",
              color: "var(--stone)",
            }}>
              Loading events...
            </div>
          ) : events.length === 0 ? (
            <div style={{
              padding: "var(--space-xl)",
              textAlign: "center",
              color: "var(--stone)",
            }}>
              No events recorded.
            </div>
          ) : (
            <div>
              {events.map((event: any) => (
                <div key={event.id} className={`event-item ${event.type}`}>
                  <div style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: 600,
                    color: "var(--ink)",
                    marginBottom: "var(--space-xs)",
                  }}>
                    {event.actorName}
                    {event.actorRole && ` (${event.actorRole})`}
                  </div>
                  <div style={{ fontSize: "var(--text-sm)", color: "var(--stone)" }}>
                    {getEventLabel(event.type)}
                  </div>
                  <div style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--stone)",
                  }}>
                    {formatTime(event.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resident Valet Feedback — only appears when service is completed and feature is enabled */}
        {request.requestType === "vehicle_retrieval" && locationId && residentId && (
          <div style={{ padding: "0 var(--space-xl) var(--space-xl)" }}>
            <ResidentValetFeedback
              locationId={locationId}
              residentId={residentId}
              serviceId={request.id}
              serviceType="retrieval"
              serviceStatus={request.status}
              serviceCompletedAt={request.completedAt ?? null}
            />
          </div>
        )}

        {/* Close Button */}
        <div style={{
          padding: "var(--space-xl)",
          borderTop: "1px solid var(--line)",
          textAlign: "center",
        }}>
          <button
            style={{
              padding: "var(--space-sm) var(--space-xl)",
              background: "var(--ink)",
              color: "var(--white)",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--text-base)",
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
